import { Check, FileText, Search } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KnowledgeSource } from '../../types/entities';
import { normalizeTags } from '../../utils';
import { Badge, Button, Input, LoadingSpinner, Modal } from '../ui';

interface KnowledgeSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  knowledgeSources: KnowledgeSource[];
  selectedKnowledgeSources: KnowledgeSource[];
  isLoading: boolean;
  onToggleSource: (source: KnowledgeSource) => void;
}

export const KnowledgeSourceModal: React.FC<KnowledgeSourceModalProps> = ({
  isOpen,
  onClose,
  knowledgeSources,
  selectedKnowledgeSources,
  isLoading,
  onToggleSource,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'ready' | 'processing' | 'error'>('all');
  const [onlySelected, setOnlySelected] = useState(false);

  const selectedIds = useMemo(
    () => new Set(selectedKnowledgeSources.map(source => source.id)),
    [selectedKnowledgeSources]
  );

  const filteredKnowledgeSources = useMemo(() => {
    return knowledgeSources.filter(source => {
      const normalizedQuery = searchQuery.trim().toLowerCase();
      if (normalizedQuery) {
        const tagString = normalizeTags(source.tags || []).join(' ').toLowerCase();
        const matchesQuery =
          source.name.toLowerCase().includes(normalizedQuery) ||
          (source.description || '').toLowerCase().includes(normalizedQuery) ||
          tagString.includes(normalizedQuery);
        if (!matchesQuery) {
          return false;
        }
      }

      if (statusFilter === 'ready') {
        if (!(source.processing_status === 'completed' && source.embeddings_status === 'completed')) {
          return false;
        }
      }

      if (statusFilter === 'processing') {
        const isProcessing =
          source.processing_status === 'processing' ||
          source.embeddings_status === 'processing';
        if (!isProcessing) {
          return false;
        }
      }

      if (statusFilter === 'error') {
        const hasError =
          source.processing_status === 'error' ||
          source.embeddings_status === 'error' ||
          Boolean(source.processing_error) ||
          Boolean(source.embeddings_error);
        if (!hasError) {
          return false;
        }
      }

      if (onlySelected && !selectedIds.has(source.id)) {
        return false;
      }

      return true;
    });
  }, [knowledgeSources, onlySelected, searchQuery, selectedIds, statusFilter]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('conversations.selectKnowledgeSources')}
      size="lg"
    >
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {selectedKnowledgeSources.length} selecionada(s)
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant={onlySelected ? 'primary' : 'outline'}
                  onClick={() => setOnlySelected(prev => !prev)}
                >
                  Somente selecionadas
                </Button>
              </div>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('conversations.selectKnowledgeDescription')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                placeholder="Filtrar por nome, descricao ou tag"
                value={searchQuery}
                onChange={event => setSearchQuery(event.target.value)}
                leftIcon={<Search className="h-4 w-4" />}
              />
              <select
                value={statusFilter}
                onChange={event =>
                  setStatusFilter(
                    event.target.value as 'all' | 'ready' | 'processing' | 'error'
                  )
                }
                className="select-control"
              >
                <option value="all">Todos os status</option>
                <option value="ready">Pronto</option>
                <option value="processing">Processando</option>
                <option value="error">Com erro</option>
              </select>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredKnowledgeSources.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                  <p>{searchQuery ? 'Nenhuma fonte encontrada para esse filtro' : t('conversations.noKnowledgeSources')}</p>
                  <p className="text-sm mt-1">
                    {t('conversations.goToKnowledgeBase')}
                  </p>
                </div>
              ) : (
                filteredKnowledgeSources.map(source => {
                  const isSelected = selectedKnowledgeSources.some(
                    s => s.id === source.id
                  );

                  return (
                    <div
                      key={source.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        isSelected
                          ? 'border-primary-500 dark:border-primary-400 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                      onClick={() => onToggleSource(source)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 dark:text-white truncate">
                            {source.name}
                          </h4>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">
                            {source.file_type} • {source.file_size} bytes
                          </p>
                          {source.tags && source.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {normalizeTags(source.tags).map((tag, index) => (
                                <Badge
                                  key={index}
                                  variant="secondary"
                                  className="text-xs"
                                >
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex-shrink-0">
                          {isSelected ? (
                            <Check className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                          ) : (
                            <div className="h-5 w-5 border-2 border-gray-300 dark:border-gray-600 rounded" />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="flex justify-end space-x-2 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="outline" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button onClick={onClose}>
                {t('conversations.confirmSelection')}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
